import { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { User, Mail, Shield, Calendar, Camera, Loader2, Save, Rocket, Upload, X } from "lucide-react";

const roleLabels = {
  admin: "Administrador",
  rd_lead: "Líder I+D",
  open_innovation_lead: "Líder Open Innovation",
  analyst: "Analista",
  stakeholder: "Stakeholder",
  viewer: "Visualizador",
  internship: "Internship",
};

const roleBadgeColors = {
  admin: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
  rd_lead: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
  open_innovation_lead: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
  analyst: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
  stakeholder: "bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-300",
  viewer: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300",
  internship: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
};

export default function ProfilePage() {
  const { user, token, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    full_name: "",
    avatar_url: "",
  });
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (user) {
      setFormData({
        full_name: user.full_name || "",
        avatar_url: user.avatar_url || "",
      });
      setPreviewUrl(user.avatar_url || null);
    }
  }, [user]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Por favor selecciona una imagen válida");
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("La imagen debe ser menor a 2MB");
      return;
    }

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/me/avatar/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formDataUpload,
      });

      if (!response.ok) {
        throw new Error('Error al subir la imagen');
      }

      const result = await response.json();
      setFormData(prev => ({ ...prev, avatar_url: result.avatar_url }));
      setPreviewUrl(result.avatar_url);
      toast.success("Imagen subida correctamente");
    } catch (error) {
      toast.error("Error al subir la imagen");
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  const clearAvatar = () => {
    setFormData(prev => ({ ...prev, avatar_url: "" }));
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const result = await api.updateMyProfile(token, formData);
      if (result.success) {
        toast.success("Perfil actualizado correctamente");
        if (refreshUser) {
          await refreshUser();
        }
      } else {
        toast.error(result.message || "Error al actualizar");
      }
    } catch (error) {
      toast.error("Error al guardar los cambios");
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return "U";
    const parts = name.split(" ");
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in max-w-2xl mx-auto" data-testid="profile-page">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary via-secondary to-accent flex items-center justify-center">
          <Rocket className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Mi Perfil</h1>
          <p className="text-muted-foreground">Gestiona tu información personal</p>
        </div>
      </div>

      {/* Profile Card */}
      <Card className="overflow-hidden">
        <div className="h-24 bg-gradient-to-r from-primary via-secondary to-accent"></div>
        <CardContent className="relative pt-0">
          <div className="flex flex-col sm:flex-row gap-6 -mt-12">
            {/* Avatar */}
            <div className="relative">
              <Avatar className="w-24 h-24 border-4 border-background shadow-xl">
                {(previewUrl || formData.avatar_url) ? (
                  <AvatarImage src={previewUrl || formData.avatar_url} alt={formData.full_name} />
                ) : null}
                <AvatarFallback className="text-2xl bg-gradient-to-br from-primary to-secondary text-white">
                  {getInitials(formData.full_name)}
                </AvatarFallback>
              </Avatar>
              <Button
                size="icon"
                variant="secondary"
                className="absolute bottom-0 right-0 rounded-full w-8 h-8 shadow-md"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Camera className="w-4 h-4" />
                )}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>

            {/* Info */}
            <div className="flex-1 pt-4 sm:pt-8">
              <h2 className="text-2xl font-bold">{formData.full_name || user.full_name}</h2>
              <div className="flex flex-wrap items-center gap-3 mt-2">
                <Badge className={roleBadgeColors[user.role]}>
                  <Shield className="w-3 h-3 mr-1" />
                  {roleLabels[user.role] || user.role}
                </Badge>
                <span className="text-muted-foreground flex items-center gap-1">
                  <Mail className="w-4 h-4" />
                  {user.email}
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Información Personal
          </CardTitle>
          <CardDescription>
            Actualiza tu nombre y foto de perfil
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="full_name">Nombre completo</Label>
              <Input
                id="full_name"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                placeholder="Tu nombre completo"
                data-testid="profile-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                value={user.email}
                disabled
                className="bg-muted"
              />
              <p className="text-xs text-muted-foreground">El email no se puede cambiar</p>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Foto de perfil</Label>
            <div className="flex flex-col gap-3">
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="flex-1"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Subiendo...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Subir desde PC
                    </>
                  )}
                </Button>
                {(previewUrl || formData.avatar_url) && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={clearAvatar}
                    title="Eliminar foto"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
              <div className="relative">
                <Input
                  id="avatar-input"
                  value={formData.avatar_url}
                  onChange={(e) => {
                    setFormData({ ...formData, avatar_url: e.target.value });
                    setPreviewUrl(e.target.value);
                  }}
                  placeholder="O introduce una URL de imagen"
                  data-testid="profile-avatar-input"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Sube una imagen desde tu ordenador (máx. 2MB) o introduce la URL de una imagen.
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => setFormData({ full_name: user.full_name, avatar_url: user.avatar_url || "" })}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={loading} data-testid="save-profile-btn">
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Guardar cambios
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Account Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Información de la Cuenta
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="p-4 rounded-lg bg-muted/50">
              <p className="text-sm text-muted-foreground">Rol actual</p>
              <p className="font-medium">{roleLabels[user.role] || user.role}</p>
            </div>
            <div className="p-4 rounded-lg bg-muted/50">
              <p className="text-sm text-muted-foreground">Miembro desde</p>
              <p className="font-medium">
                {new Date(user.created_at).toLocaleDateString('es-ES', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
